<template lang="pug">
div
  Header
  |
  .article(v-if="article")
    .article__content_inner
      BreadcrumpsCategory(:filter-query="filterQuery")
      |
      .article__image
        ImageBcgTmp(
          :image="article.cover"
          :cover-position-y="article.coverPositionY"
        )
      |
      .article__header.article__content_inner_text
        h1.article__title(v-if="article.post_title") {{ article.post_title }}
        |
        .article__meta
          .article__category(v-if="article.category?.name") {{ article.category.name }}
          |
          p.article__date(v-if="article.post_date") {{ converDate(article.post_date) }}
      |
      .article__content.article__table-of-contents.table-of-contents.article__content_inner_text(
        v-if="article.show_table_of_contents"
      )
        .table-of-contents__title In this section:
        |
        ul
          li.table-of-contents__item(
            v-for="(item, index) in article.post_content_items"
            :key="index"
            @click="scrollToSection(item.title)"
          ) {{ item.title }}
    |
    .article__content_inner
      .article__content_inner_text(v-html="article.post_content")
    |
    .article__block.article__content-items(v-if="article.post_content_items && article.show_table_of_contents")
      .article__content-item.content-item(
        v-for="(item, index) in article.post_content_items"
        :key="index"
      )
        .article__content_inner
          .article__content_inner_text
            h2.content-item__title(:id="item.title") {{ item.title }}
            |
            div(v-html="item.content")
        |
        .article__content_components
          ComponentBlocksPromo(
            v-if="item.Promo"
            :block="item.Promo"
          )
          |
          ZohoFormNewsletter(
            v-if="item.ZohoFormNewsletter"
          )
          |
          ComponentBlocksStepByStep(
            v-if="item.StepByStep"
            :block="item.StepByStep"
          )
          |
          ComponentBlocksQuote(
            v-if="item.Quote"
            :block="item.Quote"
          )
          |
          ComponentBlocksFaq(
            v-if="item.FAQ"
            :block="item.FAQ"
          )
          |
          ComponentBlocksContentImage(
            v-if="item.ContentImage"
            :block="item.ContentImage"
          )
          |
          ComponentBlocksContentCards(
            v-if="item.ContentCards"
            :block="item.ContentCards"
          )
  |
  .article__block(v-if="article.block")
    div(
      v-for="(block, index) in article.block"
      :key="`${route.fullPath}-${index}`"
    )
      component(
        :is="block.__typename"
        :key="`${route.fullPath}-${index}-${block.__typename}`"
        :block="block"
      )
    |
    div(v-if="article?.seo")
      ComponentSharedSeo(
        :seo="article.seo"
      )
    |
</template>

<script setup lang="ts">
import { useAsyncData } from '#app';

const route = useRoute();

const filterQuery = route.query.filter as string | undefined;

function converDate(isoDate: string): string {
  if (!isoDate) return "";

  const date = new Date(isoDate);

  // dd/mm/yyyy
  const formattedDate = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return formattedDate;
}

const slug = route.params.slug as string | string[] || "";

const { data: article } = await useAsyncData(`article-${slug}`, async () => {
  const articles = await queryCollection("articles").all();

  return articles[0]?.meta?.body?.find((a) => a.slug === slug) || null;
});

function scrollToSection(section: string) {
  const targetElement = document.querySelector(`h2.content-item__title[id="${section}"]`);

  if (targetElement) {
    const yOffset = -82;
    const y = targetElement.getBoundingClientRect().top + window.scrollY + yOffset;

    window.scrollTo({
      top: y,
      behavior: "smooth"
    });
  }
}
</script>

<style lang="sass" scoped>
.article
  display: grid
  grid-template-columns: repeat(12, 1fr)
  gap: 0 21px

  &__content_inner
    grid-column-start: 1
    grid-column-end: 13
    padding: 0 144px

  &__content_inner_text
    grid-column-start: 3
    grid-column-end: 11
    padding: 0 144px

  &__content_components
    margin-top: 60px

  &__content
    grid-column-start: 1
    grid-column-end: 13

    &_inner
      & :deep(h2)
        color: var(--tealgreen-900, #00261E)
        font-size: 24px
        font-style: normal
        font-weight: 800
        line-height: 29px
        margin-top: 24px
        margin-bottom: 24px

      & :deep(h3)
        color: var(--tealgreen-900, #00261E)
        font-size: 20px
        font-style: normal
        font-weight: 600
        line-height: 20px
        margin-top: 24px
        margin-bottom: 24px

      & :deep(p), & :deep(li)
        color: var(--tealgreen-900, #00261E)
        font-size: 18px
        font-style: normal
        font-weight: 400
        line-height: 26px

      & :deep(p)
        margin-top: 4px
        margin-bottom: 4px

      & :deep(a)
        cursor: pointer
        text-decoration: underline
        font-weight: 600

      & :deep(ul), & :deep(ol)
        list-style: revert-layer
        list-style-position: outside
        padding-left: 0
        margin-left: 20px
        margin-top: 24px
        margin-bottom: 24px

      & :deep(table)
        width: 100%
        border: thin solid #074033

      & :deep(td)
        border: thin solid #074033

  &__block
    grid-column-start: 1
    grid-column-end: 13

  &__image
    height: 320px
    grid-column-start: 1
    grid-column-end: 13
    padding-top: 20px
    padding-bottom: 20px

  &__header
    grid-column-start: 3
    grid-column-end: 11

  &__title
    color: var(--tealgreen-800, #074033)
    font-size: 48px
    font-style: normal
    font-weight: 800
    line-height: 56px
    padding-top: 20px
    padding-bottom: 20px

  &__meta
    margin-top: 20px
    margin-bottom: 24px
    display: flex
    gap: 16px
    align-items: center

  &__category
    display: inline-block
    color: var(--tealgreen-800, #074033)
    text-align: center
    font-size: 14px
    font-style: normal
    font-weight: 700
    line-height: 20px
    border-radius: 100px
    border: 1px solid var(--tealgreen-800, #074033)
    background: var(--limegreen-25, #F3FAE7)
    padding: 5px 12px 7px 12px
    justify-content: center
    align-items: center
    gap: 10px

  &__subtitle
    color: var(--deep-teal-900, #172621)
    font-size: 16px
    font-style: normal
    font-weight: 400
    line-height: 23px

  &__date
    color: var(--deep-teal-800, #2C403A)
    font-size: 14px
    font-style: normal
    font-weight: 400
    line-height: 20px

  &__table-of-contents
    margin-bottom: 40px

    .table-of-contents
      &__title
        color: var(--deep-teal-900, #172621)
        font-size: 18px
        font-style: normal
        font-weight: 700
        line-height: 26px
        margin-bottom: 10px

    ul 
      list-style: decimal
      list-style-position: outside
      padding-left: 0
      margin-left: 20px

      li
        color: var(--tealgreen-500, #198C6F)
        font-size: 18px
        font-style: normal
        font-weight: 400
        line-height: 26px
        margin-top: 6px
        margin-bottom: 6px
        text-indent: 4px
        cursor: pointer
        transition: 0.3s

        &:hover
          color: var(--tealgreen-700, #095745)
          text-decoration-line: underline
          text-decoration-style: solid
          text-decoration-skip-ink: none
          text-decoration-thickness: auto
          text-underline-offset: auto
          text-underline-position: from-font

        &::marker
          color: var(--deep-teal-900, #172621)
          pointer-events: none
          padding-right: 4px

  &__content-item
    .content-item
      &__title
        color: var(--tealgreen-900, #00261E)
        font-size: 24px
        font-style: normal
        font-weight: 800
        line-height: 29px
        padding-top: 54px

@media (max-width: 1024px)
  .article
    padding: 0 20px
    
@media (max-width: 768px)
  .article
    padding: 0 20px

    &__header
      grid-column-start: 1
      grid-column-end: 13

    &__content
      grid-column-start: 1
      grid-column-end: 13
      text-align: left

    &__meta
      flex-direction: column

    &__title
      font-size: 32px
      line-height: 37px

    ul
      text-align: left

    & :deep(ul), & :deep(ol)
      text-align: left
</style>